import {
  FieldLevelSearchDto,
  FieldDto,
  DatasourceDto,
  EntityCustomMetadataDto,
} from '@/api';
import { Nullable } from '@/@types/types';

export type EntityKeys =
  | keyof DatasourceDto
  | keyof FieldLevelSearchDto
  | keyof FieldDto;

export enum EntityType {
  DASHBOARD = 'dashboard',
  DATASET = 'dataset',
  DATASOURCE = 'datasource',
  CHART = 'chart',
  COLLECTION = 'collection',
  DATASET_FIELD = 'datasetFields',
  DAG = 'dags',
  RULE_RUN = 'rule',
  INCIDENT = 'incidents',
  USER = 'user',
}

const isExternalTag = (item: { type?: string }) => item.type?.endsWith('_EXTERNAL');

export const createPatchDto = <E>(
  entity: Nullable<Partial<E>>,
  currentState: any,
  removeExisting: boolean,
  isSinglePatch: boolean,
) => {
  const filteredEntries = Object.entries(entity).filter(
    ([key, value]) => key !== 'urn' && key !== 'id' && value !== undefined,
  );

  const ids: any[] = [];

  filteredEntries.forEach(([_, value]: [string, any]) => {
    if (!removeExisting && !isSinglePatch && currentState.length) {
      currentState.forEach((item: any) => {
        if (!isExternalTag(item)) {
          ids.push(item.id);
        }
      });
    }
    ids.push(
      ...value
        .filter((item: any) => !isExternalTag(item))
        .map((item: any) => item.id),
    );
  });

  return ids;
};

export const createCustomMetadataPatchDto = (
  customMetadata: EntityCustomMetadataDto[],
  currentStateCustomMetadata: EntityCustomMetadataDto[] | undefined,
): string[] => {
  const EXCLUDED_LINEAGE_PLATFORMS = new Set<EntityCustomMetadataDto.lineagePlatform>([
    EntityCustomMetadataDto.lineagePlatform.ATLAN,
    EntityCustomMetadataDto.lineagePlatform.ALATION,
    EntityCustomMetadataDto.lineagePlatform.AMUNDSEN,
    EntityCustomMetadataDto.lineagePlatform.APACHE_ATLAS,
    EntityCustomMetadataDto.lineagePlatform.CASTOR_DOC,
    EntityCustomMetadataDto.lineagePlatform.COLLIBRA,
    EntityCustomMetadataDto.lineagePlatform.DATAGALAXY,
    EntityCustomMetadataDto.lineagePlatform.INFORMATICA,
    EntityCustomMetadataDto.lineagePlatform.SECODA,
    EntityCustomMetadataDto.lineagePlatform.SELECT_STAR,
    EntityCustomMetadataDto.lineagePlatform.CATALOG_GENERIC,
  ]);

  const customMetadataListToIds = (items: EntityCustomMetadataDto[]) => items.flatMap(({ entries }) => entries.map(({ id }) => id));

  const externalCustomMetadataIds = customMetadataListToIds(
    currentStateCustomMetadata?.filter(({ lineagePlatform }) => lineagePlatform && EXCLUDED_LINEAGE_PLATFORMS.has(lineagePlatform)) ?? [],
  );
  const siffletEditableCustomMetadataIds = customMetadataListToIds(
    customMetadata.filter(({ lineagePlatform }) => !lineagePlatform || !EXCLUDED_LINEAGE_PLATFORMS.has(lineagePlatform)),
  );
  return [...siffletEditableCustomMetadataIds, ...externalCustomMetadataIds];
};
